import {
  MACRO_TEXT_BACKGROUND,
  MACRO_TEXT_COLOR,
  MACRO_VARIABLES,
  MacroVariable as MacroVariableType,
} from "@redotech/redo-model/macro";
import Quill from "quill";
import { memo } from "react";
import { Flex } from "./flex";
import { Text } from "./text";

export const MACRO_LINK_VARIABLES = ["recent_order_tracking_url_link"];

export const MacroVariablePill = memo(function MacroVariablePill({
  variable,
}: {
  variable: MacroVariableType;
}) {
  return (
    <Text
      as="span"
      fontSize="xs"
      style={{
        backgroundColor: MACRO_TEXT_BACKGROUND,
        color: MACRO_TEXT_COLOR,
        borderRadius: "var(--redo-corner-xxsmall-radius)",
        display: "inline-block",
        padding: "2px 6px",
      }}
    >
      {variable.displayName}
    </Text>
  );
});

export const MacroVariableList = memo(function MacroVariableList({
  onVariableClick,
}: {
  onVariableClick: (variable: MacroVariableType) => void;
}) {
  return (
    <Flex dir="column" gap="xs">
      {[...MACRO_VARIABLES]
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .map((variable) => (
          <Flex
            cursor="pointer"
            hoverColor="secondary"
            key={variable.id}
            onClick={() => onVariableClick(variable)}
            p="xs"
            radius="xs"
          >
            <MacroVariablePill variable={variable} />
          </Flex>
        ))}
    </Flex>
  );
});

export const MacroVariableSidebar = memo(function MacroVariableSidebar({
  onVariableClick,
}: {
  onVariableClick: (variable: MacroVariableType) => void;
}) {
  return (
    <Flex
      borderColor="secondary"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="none"
      radius="sm"
      w="full"
    >
      <Flex
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        p="sm"
      >
        <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
          Variables
        </Text>
      </Flex>
      <Flex dir="column" overflow="auto" p="xs">
        <MacroVariableList onVariableClick={onVariableClick} />
      </Flex>
    </Flex>
  );
});

export function insertMacroVariableIntoQuill(
  quill: Quill,
  variable: MacroVariableType,
): void {
  const variableText = variable.variable;
  const insertPosition = quill.getSelection(true)?.index || 0;

  if (MACRO_LINK_VARIABLES.includes(variableText)) {
    quill.insertText(insertPosition, variableText, "silent");
    quill.formatText(
      insertPosition,
      variableText.length,
      {
        color: MACRO_TEXT_COLOR,
        background: MACRO_TEXT_BACKGROUND,
        link: variableText,
        target: "_blank",
        rel: "noopener noreferrer",
      },
      "silent",
    );
  } else {
    quill.insertText(insertPosition, variableText, "silent");
    quill.formatText(
      insertPosition,
      variableText.length,
      { color: MACRO_TEXT_COLOR, background: MACRO_TEXT_BACKGROUND },
      "silent",
    );
  }

  clearColoringAtCursor(quill, insertPosition + variableText.length);
}

export function clearColoringAtCursor(
  quill: Quill,
  newCursorPosition: number,
): void {
  quill.setSelection(newCursorPosition, 0);
  quill.format("color", false);
  quill.format("background", false);
}

export function clearFormattingFromMacroAutomationsText(quill: Quill): void {
  const content = quill.getContents();
  const ops = content.ops;

  for (const op of ops) {
    if (typeof op.insert === "string") {
      const attributes = op.attributes;
      if (attributes && attributes.color === MACRO_TEXT_COLOR) {
        attributes.color = "black";
      }
      if (attributes && attributes.background === MACRO_TEXT_BACKGROUND) {
        attributes.background = "inherit";
      }
    }
  }

  quill.setContents(quill.getContents());
}

export {
  MACRO_TEXT_BACKGROUND,
  MACRO_TEXT_COLOR,
} from "@redotech/redo-model/macro";
